import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import QRCode from 'qrcode';
import { Copy, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { LOCALE_NAMES, SUPPORTED_LOCALES, type Locale } from '@zt/i18n';
import { Button, Field, Input, Select, Spinner } from '@zt/ui';
import { api, ApiError } from '../lib/api';
import { HelpTip } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { useInvalidateSession, useSession } from '../lib/session';
import { rememberedSystemName } from '../lib/system-name';

function AuthLayout({ title, lead, children }: { title: string; lead?: ReactNode; children: ReactNode }) {
  const { t, locale, setLocale } = useI18n();
  return (
    <div className="grid min-h-dvh lg:grid-cols-[minmax(20rem,28rem)_1fr]">
      <aside className="relative hidden overflow-hidden bg-pine p-10 text-on-pine lg:flex lg:flex-col lg:justify-between">
        <div className="flex items-center gap-3">
          <svg viewBox="0 0 32 32" className="h-9 w-9" aria-hidden>
            <rect width="32" height="32" rx="7" fill="currentColor" opacity="0.14" />
            <path
              d="M9 10h14l-11 12h11"
              fill="none"
              stroke="currentColor"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="font-cond text-xl font-semibold">
            {rememberedSystemName() ?? t('admin.auth.brand')}
          </span>
        </div>
        <p className="max-w-[22ch] font-cond text-[2rem] font-semibold leading-[1.15]">
          {t('admin.auth.tagline')}
        </p>
        {/* Prospektüs şeridi: kutudan çıkan kullanım talimatı çizgisi */}
        <div className="space-y-1.5 opacity-60" aria-hidden>
          {[92, 78, 85, 60].map((w) => (
            <div key={w} className="h-1 rounded-full bg-current" style={{ width: `${w}%` }} />
          ))}
        </div>
      </aside>
      <main className="flex flex-col px-5 py-8 sm:px-10">
        <div className="flex justify-end">
          <Select
            aria-label={t('common.language')}
            value={locale}
            onChange={(e) => setLocale(e.target.value as Locale)}
            className="h-9 w-auto text-sm"
          >
            {SUPPORTED_LOCALES.map((l) => (
              <option key={l} value={l}>
                {LOCALE_NAMES[l]}
              </option>
            ))}
          </Select>
        </div>
        <div className="mx-auto flex w-full max-w-[26rem] flex-1 flex-col justify-center py-10">
          <h1 className="font-cond text-[1.875rem] font-semibold leading-tight tracking-[-0.01em]">
            {title}
          </h1>
          {lead && <div className="mt-2 text-ink-2">{lead}</div>}
          <div className="mt-7">{children}</div>
        </div>
      </main>
    </div>
  );
}

/** MFA ekranları yalnızca şifreyle açılmış bir oturumla anlamlıdır; oturum yoksa girişe döner. */
function useNeedsSession(): boolean {
  const session = useSession();
  return !session.isLoading && !session.data;
}

function errorText(e: unknown): string | undefined {
  return e instanceof ApiError ? e.title : e ? String(e) : undefined;
}

export function LoginPage() {
  const t = useT();
  const navigate = useNavigate();
  const invalidate = useInvalidateSession();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.post<{ status: 'mfa_required' | 'mfa_setup_required' }>('/v1/auth/sign-in', {
        email,
        password,
      });
      await invalidate();
      await navigate({ to: r.status === 'mfa_required' ? '/mfa' : '/mfa-kurulum' });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <AuthLayout title={t('admin.auth.signInTitle')}>
      <form onSubmit={submit} className="space-y-5" noValidate>
        <Field label={t('common.email')} required>
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          )}
        </Field>
        <Field label={t('common.password')} required error={error} help={<HelpTip k="auth.sign_in" />}>
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          )}
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          {t('admin.auth.signIn')}
        </Button>
        <p className="text-center text-sm">
          <Link to="/sifremi-unuttum" className="text-pine underline-offset-2 hover:underline">
            {t('admin.auth.forgot')}
          </Link>
        </p>
      </form>
    </AuthLayout>
  );
}

export function MfaPage() {
  const t = useT();
  const navigate = useNavigate();
  const invalidate = useInvalidateSession();
  const [code, setCode] = useState('');
  const [backup, setBackup] = useState(false);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const noSession = useNeedsSession();
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      await api.post('/v1/auth/mfa/verify', backup ? { backupCode: code } : { code: code.trim() }, {
        noStepUp: true,
      });
      await invalidate();
      await navigate({ to: '/' });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  if (noSession) return <Navigate to="/giris" />;
  return (
    <AuthLayout title={t('admin.auth.mfaTitle')} lead={t('admin.auth.mfaLead')}>
      <form onSubmit={submit} className="space-y-5">
        <Field
          label={backup ? t('admin.auth.backupCode') : t('admin.auth.code')}
          required
          error={error}
          help={<HelpTip k="auth.mfa_verify" />}
        >
          {(p) => (
            <Input
              {...p}
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode={backup ? 'text' : 'numeric'}
              autoComplete="one-time-code"
              maxLength={backup ? 12 : 6}
              className="tabular h-12 text-xl tracking-[0.35em]"
            />
          )}
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          {t('admin.auth.verify')}
        </Button>
        <button
          type="button"
          className="block w-full text-center text-sm text-pine underline-offset-2 hover:underline"
          onClick={() => setBackup((v) => !v)}
        >
          {backup ? t('admin.auth.useCode') : t('admin.auth.useBackup')}
        </button>
      </form>
    </AuthLayout>
  );
}

export function MfaEnrollPage() {
  const t = useT();
  const navigate = useNavigate();
  const invalidate = useInvalidateSession();
  const [qr, setQr] = useState<string>();
  const [secret, setSecret] = useState<string>();
  const [code, setCode] = useState('');
  const [codes, setCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);
  const noSession = useNeedsSession();

  useEffect(() => {
    // Sunucu tarafı idempotenttir: StrictMode'daki çift çağrı da aynı anahtarı döndürür.
    let alive = true;
    api
      .post<{ secret: string; uri: string }>('/v1/auth/mfa/totp/enroll')
      .then(async (r) => {
        if (!alive) return;
        setSecret(r.secret);
        setQr(
          await QRCode.toDataURL(r.uri, {
            margin: 1,
            width: 220,
            color: { dark: '#16211d', light: '#ffffff' },
          }),
        );
      })
      .catch((e) => {
        if (e instanceof ApiError && e.code === 'mfa_already_enrolled') void navigate({ to: '/mfa' });
        else setError(errorText(e));
      });
    return () => {
      alive = false;
    };
  }, [navigate]);

  const confirm = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.post<{ backupCodes: string[] }>('/v1/auth/mfa/totp/confirm', { code: code.trim() });
      setCodes(r.backupCodes);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  if (noSession && !codes) return <Navigate to="/giris" />;
  if (codes) {
    return (
      <AuthLayout title={t('admin.auth.backupTitle')} lead={t('admin.auth.backupLead')}>
        <ol
          className="tabular grid grid-cols-2 gap-2 rounded-md border border-line bg-surface p-4 font-medium"
          aria-label={t('admin.auth.backupCode')}
        >
          {codes.map((c) => (
            <li key={c} className="rounded-sm bg-surface-2 px-3 py-2 text-center tracking-wider">
              {c}
            </li>
          ))}
        </ol>
        <div className="mt-4 flex flex-wrap gap-2">
          <Button
            onClick={async () => {
              await navigator.clipboard.writeText(codes.join('\n'));
              toast.success(t('common.copied'));
            }}
          >
            <Copy className="h-4 w-4" aria-hidden /> {t('admin.auth.copyCodes')}
          </Button>
          <Button onClick={() => window.print()}>
            <Printer className="h-4 w-4" aria-hidden /> {t('admin.auth.printCodes')}
          </Button>
        </div>
        <Button
          variant="primary"
          size="lg"
          className="mt-6 w-full"
          onClick={async () => {
            await invalidate();
            await navigate({ to: '/' });
          }}
        >
          {t('admin.auth.backupDone')}
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t('admin.auth.enrollTitle')} lead={t('admin.auth.enrollLead')}>
      {!qr && !error && <Spinner label={t('common.loading')} />}
      {qr && (
        <div className="mb-6 flex flex-col items-center gap-3 rounded-md border border-line bg-surface p-5 sm:flex-row sm:items-start">
          <img src={qr} alt="" width={180} height={180} className="rounded-sm" />
          <div className="text-sm text-ink-2">
            <p>{t('admin.auth.manualKey')}</p>
            <code className="tabular mt-2 block break-all rounded-sm bg-surface-2 px-2 py-1.5 text-ink">
              {secret}
            </code>
          </div>
        </div>
      )}
      <form onSubmit={confirm} className="space-y-5">
        <Field label={t('admin.auth.code')} required error={error} help={<HelpTip k="auth.mfa_enroll" />}>
          {(p) => (
            <Input
              {...p}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              className="tabular h-12 text-xl tracking-[0.35em]"
            />
          )}
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy} disabled={!qr}>
          {t('admin.auth.confirmEnroll')}
        </Button>
      </form>
    </AuthLayout>
  );
}

export function ForgotPage() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState<null | 'email' | 'unavailable'>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  return (
    <AuthLayout title={t('admin.auth.forgotTitle')} lead={t('admin.auth.forgotLead')}>
      {sent ? (
        <p className="rounded-md border border-line bg-insert p-4 text-ink" role="status">
          {sent === 'unavailable' ? t('admin.auth.forgotUnavailable') : t('admin.auth.forgotSent')}
        </p>
      ) : (
        <form
          className="space-y-5"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            try {
              const r = await api.post<{ delivery?: 'email' | 'unavailable' }>('/v1/auth/password/forgot', {
                email,
              });
              setSent(r.delivery === 'unavailable' ? 'unavailable' : 'email');
            } catch (err) {
              setError(errorText(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field label={t('common.email')} required error={error} help={<HelpTip k="auth.forgot_password" />}>
            {(p) => (
              <Input
                {...p}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
            {t('admin.auth.sendLink')}
          </Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm">
        <Link to="/giris" className="text-pine underline-offset-2 hover:underline">
          {t('admin.auth.backToSignIn')}
        </Link>
      </p>
    </AuthLayout>
  );
}

export function ResetPage() {
  const t = useT();
  const { token } = useSearch({ strict: false }) as { token?: string };
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthLayout title={t('admin.auth.resetTitle')}>
      {done ? (
        <p className="rounded-md border border-line bg-insert p-4" role="status">
          {t('admin.auth.resetDone')}
        </p>
      ) : (
        <form
          className="space-y-5"
          onSubmit={async (e) => {
            e.preventDefault();
            setBusy(true);
            setErr(null);
            try {
              await api.post('/v1/auth/password/reset', { token: token ?? '', password });
              setDone(true);
            } catch (e2) {
              setErr(e2 instanceof ApiError ? e2 : null);
            } finally {
              setBusy(false);
            }
          }}
        >
          <Field
            label={t('admin.auth.newPassword')}
            required
            description={t('admin.auth.passwordHint')}
            error={err?.fieldError('password') ?? err?.title}
          >
            {(p) => (
              <Input
                {...p}
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            )}
          </Field>
          <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
            {t('admin.auth.setPassword')}
          </Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm">
        <Link to="/giris" className="text-pine underline-offset-2 hover:underline">
          {t('admin.auth.backToSignIn')}
        </Link>
      </p>
    </AuthLayout>
  );
}

export function InvitePage() {
  const t = useT();
  const navigate = useNavigate();
  const invalidate = useInvalidateSession();
  const { token = '' } = useSearch({ strict: false }) as { token?: string };
  const info = useQuery({
    queryKey: ['invitation', token],
    queryFn: () =>
      api.get<{ organizationName: string; email: string; roleKey: string; userExists: boolean }>(
        `/v1/auth/invitations/${encodeURIComponent(token)}`,
      ),
    retry: false,
    enabled: token.length > 10,
  });
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  if (info.isError || token.length <= 10) {
    return (
      <AuthLayout title={t('errors.token_invalid')}>
        <Link to="/giris" className="text-pine underline-offset-2 hover:underline">
          {t('admin.auth.backToSignIn')}
        </Link>
      </AuthLayout>
    );
  }
  if (!info.data) return <Spinner label={t('common.loading')} />;
  const d = info.data;
  return (
    <AuthLayout
      title={t('admin.auth.inviteTitle', { organization: d.organizationName })}
      lead={t(d.userExists ? 'admin.auth.inviteExistingLead' : 'admin.auth.inviteLead', { email: d.email })}
    >
      <form
        className="space-y-5"
        onSubmit={async (e) => {
          e.preventDefault();
          setBusy(true);
          setErr(null);
          try {
            const r = await api.post<{ status: string }>('/v1/auth/invitations/accept', {
              token,
              name: d.userExists ? undefined : name,
              password,
            });
            await invalidate();
            await navigate({ to: r.status === 'mfa_required' ? '/mfa' : '/mfa-kurulum' });
          } catch (e2) {
            setErr(e2 instanceof ApiError ? e2 : null);
          } finally {
            setBusy(false);
          }
        }}
      >
        <p className="text-sm text-ink-2">
          {t('admin.users.role')}: <span className="font-medium text-ink">{t(`roles.${d.roleKey}`)}</span>
        </p>
        {!d.userExists && (
          <Field label={t('admin.auth.yourName')} required error={err?.fieldError('name')}>
            {(p) => (
              <Input {...p} autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} />
            )}
          </Field>
        )}
        <Field
          label={d.userExists ? t('common.password') : t('admin.auth.newPassword')}
          required
          description={d.userExists ? undefined : t('admin.auth.passwordHint')}
          error={err?.fieldError('password') ?? (err && !err.errors.length ? err.title : undefined)}
        >
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete={d.userExists ? 'current-password' : 'new-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}
        </Field>
        <Button type="submit" variant="primary" size="lg" className="w-full" loading={busy}>
          {t('admin.auth.acceptInvite')}
        </Button>
      </form>
    </AuthLayout>
  );
}
